import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { api, ACTIVE_ORG_STORAGE_KEY } from './api';
import { supabase } from './supabaseClient';
import { Telemetry, getSessionDurationSeconds } from './telemetry';
import { createLogger } from './debug';
import { stashPostAuthRedirect } from './postAuthRedirect';
import { API_BASE } from '@/design/tokens';
import type { AuthUser } from '@/types/api';

export type { AuthUser };

const log = createLogger('auth');

const IS_LOCAL_DEV = import.meta.env.VITE_LOCAL_DEV === 'true';

// A2F-422: shallow-compare AuthUser to skip setUser when nothing material changed.
// Used by the init effect, onAuthStateChange SIGNED_IN, and refreshAuth to avoid
// spreading a fresh object reference into state when the result is equivalent to
// what's already there (which would re-render every AuthContext consumer).
const sameAuthUser = (a: AuthUser | null, b: AuthUser | null): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.id === b.id &&
    a.email === b.email &&
    a.displayName === b.displayName &&
    a.role === b.role &&
    a.orgId === b.orgId &&
    a.activeOrgId === b.activeOrgId &&
    a.authMethod === b.authMethod &&
    // orgs: reference equality intentional — backend returns a fresh array per
    // fetch, so real membership changes still rerender; the dedupe cache-hit
    // path returns the same array ref, which is exactly when we want to skip.
    a.orgs === b.orgs
  );
};

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  authRequired: boolean;
  /** Error message when auth fails (e.g. deactivated account). Shown on login page. */
  authError: string | null;
  /** Entra ID access token for Microsoft Graph API (delegated). Null after page refresh. */
  graphToken: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  /** Re-authenticate with Microsoft to get a fresh Graph token. */
  reconnectGraph: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  authRequired: false,
  authError: null,
  graphToken: null,
  login: async () => {},
  logout: async () => {},
  refreshAuth: async () => {},
  reconnectGraph: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [graphToken, setGraphToken] = useState<string | null>(null);

  // A2F-323: Deduplicate /auth/me calls — track last fetched user ID and in-flight request.
  // A2F-422: cache the fetched user in a ref (not React state) so the dedupe callback
  // stays stable across renders and doesn't put the init effect in a rebuild loop.
  const lastFetchedUserIdRef = useRef<string | null>(null);
  const lastFetchedUserRef = useRef<AuthUser | null>(null);
  const fetchInFlightRef = useRef<Promise<AuthUser | null> | null>(null);

  /** Fetch Chrona user from /me (org_id, role come from backend). */
  const fetchChronaUser = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const me = await api.auth.me();
      setAuthError(null);
      if (!me) return null;
      return {
        id: me.id,
        email: me.email,
        displayName: me.displayName,
        role: me.role,
        orgId: me.orgId,
        activeOrgId: me.activeOrgId ?? me.orgId,
        orgs: me.orgs ?? [],
      };
    } catch (err) {
      // Surface 403 messages (e.g. deactivated account, unauthorized) to the login page
      if (err instanceof Error && err.message.includes('403')) {
        const detail = err.message.replace(/^API error 403:\s*/, '');
        // Remap backend messages to user-friendly versions
        if (detail.includes('not authorized') || detail.includes('not been configured')) {
          setAuthError('Your Microsoft account is not authorized for Chrona. Please contact your organization\'s administrator.');
        } else if (detail.includes('deactivated')) {
          setAuthError('This account has been deactivated. Please contact your organization\'s administrator.');
        } else if (detail.includes('requires SSO')) {
          setAuthError('Your organization requires Microsoft SSO. Please use the "Sign in with Microsoft" button.');
        } else if (detail.includes('web app access')) {
          setAuthError('Your account does not have web app access. Please contact your organization\'s administrator.');
        } else {
          setAuthError(detail);
        }
      }
      return null;
    }
  }, []);

  /**
   * Deduplicated fetch — skips if we already fetched for this Supabase user ID,
   * and reuses in-flight requests to prevent concurrent /auth/me calls.
   */
  const fetchChronaUserDeduped = useCallback(async (supabaseUserId: string): Promise<AuthUser | null> => {
    // Skip if we already fetched for this user and have a result
    if (lastFetchedUserIdRef.current === supabaseUserId && lastFetchedUserRef.current) {
      log.log('fetchChronaUser deduped — already fetched for', supabaseUserId);
      return lastFetchedUserRef.current;
    }
    // Reuse in-flight request
    if (fetchInFlightRef.current) {
      log.log('fetchChronaUser deduped — reusing in-flight request');
      return fetchInFlightRef.current;
    }
    const promise = fetchChronaUser();
    fetchInFlightRef.current = promise;
    try {
      const result = await promise;
      lastFetchedUserIdRef.current = supabaseUserId;
      lastFetchedUserRef.current = result;
      return result;
    } finally {
      fetchInFlightRef.current = null;
    }
  }, [fetchChronaUser]);

  /** Restore session on mount. */
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      log.log('init() start');

      // ── Local dev mode: skip Supabase, call /auth/me directly ──
      if (IS_LOCAL_DEV) {
        log.log('LOCAL_DEV mode — skipping Supabase, calling /auth/me directly');
        try {
          const chronaUser = await fetchChronaUser();
          if (chronaUser && !cancelled) {
            setUser(chronaUser);
            log.log('LOCAL_DEV auth OK:', chronaUser.email);
          }
        } catch {
          if (!cancelled) {
            setAuthError(`Backend not reachable at ${API_BASE}`);
          }
        }
        if (!cancelled) setLoading(false);
        return;
      }

      // ── Production: Supabase auth flow ──
      // Check if auth is configured at all
      try {
        const status = await log.time('setupStatus', () => api.auth.setupStatus());
        log.log('setupStatus result', status);
        if (status.authRequired) {
          setAuthRequired(true);
        }
      } catch (err) {
        // Backend might not support auth — continue without auth
        log.warn('setupStatus failed, continuing without auth', err);
        setLoading(false);
        return;
      }

      // Check for existing Supabase session (with timeout — Web Locks can deadlock)
      log.log('checking supabase session');
      let session = null;
      try {
        const result = await log.time('getSession', () =>
          Promise.race([
            supabase.auth.getSession(),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('getSession timeout (5s)')), 5000)
            ),
          ])
        );
        session = result.data.session;
      } catch (err) {
        log.warn('getSession timed out — treating as no session', err);
      }
      log.log('supabase session:', session ? 'found' : 'none');
      if (session && !cancelled) {
        const chronaUser = await log.time('fetchChronaUser', () =>
          fetchChronaUserDeduped(session.user.id)
        );
        log.log('chrona user:', chronaUser ? chronaUser.email : 'null');
        if (chronaUser) {
          const authMethod = (session.user.app_metadata?.chrona_auth_method as 'sso' | 'email') || null;
          const next: AuthUser = { ...chronaUser, authMethod };
          setUser(prev => (sameAuthUser(prev, next) ? prev : next));
          setAuthRequired(true);
          const telemetryOrgId = chronaUser.activeOrgId ?? chronaUser.orgId;
          Telemetry.identify(chronaUser.id, {
            role: chronaUser.role,
            email: chronaUser.email,
            org_id: telemetryOrgId,
          });
          Telemetry.setOrgGroup(telemetryOrgId);
          Telemetry.registerSuperProperties({ orgId: telemetryOrgId, userRole: chronaUser.role });
        }
      }
      if (!cancelled) {
        log.log('auth init complete, setLoading(false)');
        setLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
  }, [fetchChronaUser, fetchChronaUserDeduped]);

  /**
   * Listen for Supabase auth state changes (login, logout, token refresh).
   *
   * A2F-625 Layer 2 note — password-recovery sessions:
   *
   * When a commons user clicks a reset link whose `redirect_to` mistakenly
   * points at the main app, Supabase drops the user here with a URL hash
   * like `#access_token=…&type=recovery`. The JS SDK auto-exchanges the
   * hash BEFORE this listener fires, so by the time we see an event it's
   * a bare `SIGNED_IN` — indistinguishable from a fresh login. A2F-520's
   * commons-only redirect (in AuthGate) will then bounce the user to
   * commons, but by then the recovery token is already consumed and
   * commons has no hash to drive UpdatePasswordPage. Net: the user is
   * signed-in on commons with no password-reset UI.
   *
   * This is defense-in-depth only — A2F-625 Layer 1 (correct `redirectTo`
   * on the commons reset page + full URL allowlist in both Supabase
   * projects) keeps the email from landing here in the first place. A
   * proper Layer 2 fix would either (a) detect `?type=recovery` in the
   * URL hash BEFORE letting the SDK exchange it and route commons-only
   * users straight to commons with the hash intact, or (b) give the main
   * app its own UpdatePasswordPage that refuses to render for commons-
   * only users and bounces them with the recovery hash preserved. Both
   * touch the main-app auth boot sequence, which is high blast radius;
   * deferred until we see real evidence of the Layer 1 allowlist drifting
   * again. If you're here because it drifted: open a Linear ticket and
   * do this properly rather than hacking the `onAuthStateChange` branch.
   */
  useEffect(() => {
    // Local dev mode: no Supabase listener needed
    if (IS_LOCAL_DEV) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          // Capture the Entra ID provider token (only available immediately after OAuth login)
          if (session.provider_token) {
            setGraphToken(session.provider_token);
          }
          const chronaUser = await fetchChronaUserDeduped(session.user.id);
          if (chronaUser) {
            const authMethod = (session.user.app_metadata?.chrona_auth_method as 'sso' | 'email') || null;
            const next: AuthUser = { ...chronaUser, authMethod };
            setUser(prev => (sameAuthUser(prev, next) ? prev : next));
            setAuthRequired(true);
            const telemetryOrgId = chronaUser.activeOrgId ?? chronaUser.orgId;
            Telemetry.identify(chronaUser.id, {
              role: chronaUser.role,
              email: chronaUser.email,
              org_id: telemetryOrgId,
            });
            Telemetry.setOrgGroup(telemetryOrgId);
            Telemetry.registerSuperProperties({ orgId: telemetryOrgId, userRole: chronaUser.role });
            Telemetry.userSignedIn({ auth_method: 'sso_entra_id' });
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setGraphToken(null);
          lastFetchedUserIdRef.current = null;
          lastFetchedUserRef.current = null;
        }
        // TOKEN_REFRESHED is handled automatically by Supabase — no action needed
      }
    );

    return () => subscription.unsubscribe();
  }, [fetchChronaUserDeduped]);

  /** Sign in via Entra ID (Supabase handles OAuth/OIDC). No-op in local dev. */
  const login = async () => {
    if (IS_LOCAL_DEV) return;
    await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email profile openid Sites.Read.All',
        redirectTo: window.location.origin + '/auth/callback',
      },
    });
  };

  /** Re-authenticate to get a fresh Graph API token (provider_token is lost on page refresh). */
  const reconnectGraph = async () => {
    // Remember where the reconnect was triggered from (e.g. /fre mid-form) so the
    // OAuth callback returns the user here instead of the dashboard. Unlike
    // login(), reconnect is always triggered mid-app, so a return path is
    // meaningful. Hash is omitted — Supabase overwrites it with the token fragment.
    stashPostAuthRedirect(window.location.pathname + window.location.search);
    await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email profile openid Sites.Read.All',
        redirectTo: window.location.origin + '/auth/callback',
      },
    });
  };

  const logout = async () => {
    Telemetry.userSignedOut({ session_duration_seconds: getSessionDurationSeconds() });
    Telemetry.reset();
    if (!IS_LOCAL_DEV) {
      await supabase.auth.signOut();
    }
    // A2F-244: clear any stored X-Org-Id selection so the next user who
    // signs in on this device doesn't inherit someone else's org choice.
    try { localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY); } catch { /* ignore */ }
    setUser(null);
  };

  /** Re-fetch the Chrona user (e.g., after OAuth callback). */
  const refreshAuth = useCallback(async () => {
    // Local dev: just re-call /auth/me directly
    if (IS_LOCAL_DEV) {
      const chronaUser = await fetchChronaUser();
      if (chronaUser) setUser(chronaUser);
      return;
    }

    try {
      const result = await Promise.race([
        supabase.auth.getSession(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('getSession timeout')), 5000)
        ),
      ]);
      if (result.data.session) {
        // Force re-fetch by clearing the dedupe cache
        lastFetchedUserIdRef.current = null;
        lastFetchedUserRef.current = null;
        const chronaUser = await fetchChronaUserDeduped(result.data.session.user.id);
        if (chronaUser) {
          const authMethod = (result.data.session.user.app_metadata?.chrona_auth_method as 'sso' | 'email') || null;
          const next: AuthUser = { ...chronaUser, authMethod };
          setUser(prev => (sameAuthUser(prev, next) ? prev : next));
          setAuthRequired(true);
        }
      }
    } catch {
      log.warn('refreshAuth: getSession timed out');
    }
  }, [fetchChronaUser, fetchChronaUserDeduped]);

  return (
    <AuthContext.Provider value={{ user, loading, authRequired, authError, graphToken, login, logout, refreshAuth, reconnectGraph }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
